import { useEffect, useState } from "react";
import {
  type ReserveConfigResponse,
  fetchReserveConfig,
  resetReserveConfig,
  saveReserveConfig,
} from "../api";
import { SectionTitle } from "./ui";

/**
 * Account-section: battery reserve for a power cut. The installer sets
 * the battery's floor; the customer may raise it so more energy stays
 * in the pack (Stefan, 2026-10-04). Same override precedent as
 * EvSettings: stored via the backend on ems-engine's PVC, "Terug naar
 * standaard" reverts. The plan, the inverter windows and "Nu ontladen"
 * all stop at this floor.
 */
export function ReserveSettings() {
  const [state, setState] = useState<ReserveConfigResponse | null>(null);
  const [value, setValue] = useState<number>(10);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);

  useEffect(() => {
    fetchReserveConfig()
      .then((s) => {
        setState(s);
        setValue(Math.round(s.min_soc_pct));
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  async function run(op: () => Promise<ReserveConfigResponse>, flash: string) {
    setBusy(true);
    setError(null);
    try {
      const s = await op();
      setState(s);
      setValue(Math.round(s.min_soc_pct));
      setSavedFlash(flash);
      window.setTimeout(() => setSavedFlash(null), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const lo = Math.round(state?.default_min_soc_pct ?? 5);
  const hi = Math.round(state?.max_pct ?? 80);
  const dirty = state != null && value !== Math.round(state.min_soc_pct);

  return (
    <section className="flex flex-col gap-2">
      <SectionTitle>Reserve bij stroomuitval</SectionTitle>
      <div className="card p-4 flex flex-col gap-3">
        <p className="text-xs text-ink-mute leading-relaxed">
          De batterij wordt door de planning nooit verder ontladen dan dit
          percentage. Hoger = meer reserve als de stroom uitvalt, minder
          ruimte om met de stroomprijs te handelen. De installateur heeft
          de ondergrens op {lo}% gezet; daaronder kunt u niet.
        </p>
        <label className="flex items-center justify-between gap-4">
          <span className="text-sm font-medium">Minimaal in de batterij</span>
          <span className="display-num text-2xl text-ink tabular">{value}%</span>
        </label>
        <input
          type="range"
          min={lo}
          max={hi}
          step={5}
          value={value}
          disabled={busy || state == null}
          onChange={(e) => setValue(Number(e.target.value))}
          className="w-full accent-[var(--color-accent)]"
          aria-label="Minimale batterijlading in procenten"
        />
        <div className="flex justify-between text-[11px] text-ink-mute tabular">
          <span>{lo}%</span>
          <span>{hi}%</span>
        </div>

        {error && <div className="text-sm text-danger">{error}</div>}

        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            disabled={busy || state?.source !== "override"}
            onClick={() => void run(resetReserveConfig, "Teruggezet naar standaard")}
            className="text-sm text-ink-mute disabled:opacity-40 hover:text-ink"
          >
            Terug naar standaard
          </button>
          <div className="flex items-center gap-3">
            {savedFlash && <span className="text-xs text-accent">{savedFlash}</span>}
            <button
              type="button"
              disabled={busy || !dirty}
              onClick={() => void run(() => saveReserveConfig(value), "Opgeslagen")}
              className="bg-accent text-white text-sm rounded-full px-4 py-2 font-medium disabled:opacity-60"
            >
              Opslaan
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
